import { FC } from 'react';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { Button } from '@jobcost-monorepo/ui';
import { useFormContext } from 'react-hook-form';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import dayjs from '@jobcost-monorepo/dayjs-config';
import { countBusinessDays } from '@jobcost-monorepo/utils';
import { createRepository } from '../../../dto/repositoryFactory';
import {
  ICustomer,
  IJob,
  IListCustomerFetchParams,
  IListJobFetchParams,
} from '../../../dto';
import { ApiResponse, MN_CUSTOMERS, MN_JOBS } from '../../../common';
import { AxiosError } from 'axios';
import { handleAxiosError } from '../../../utils';

const CreateOrderDocButton: FC = () => {
  const { t } = useTranslation('orders');
  const { watch } = useFormContext();
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const code = watch('code');
  const typeOfPayment = watch('typeOfPayment');
  const resource = watch('resource');
  const dailyRevenue = watch('dailyRevenue');
  const technology = watch('technology');
  const jobId = watch('jobId');

  const loadFile = async (url: string): Promise<ArrayBuffer> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}`);
    }
    return await response.arrayBuffer();
  };

  const getCustomerData = async () => {
    if (jobId === null || jobId === undefined) {
      return;
    }

    const repository = createRepository<IJob, IListJobFetchParams>(MN_JOBS);
    const customerId = await repository
      .get(jobId)
      .then((response: ApiResponse<IJob>) => {
        if (response?.data) {
          const job = response?.data;
          return job?.customerId;
        }
      })
      .catch((err: AxiosError) => handleAxiosError(err));

    if (customerId) {
      const repository = createRepository<ICustomer, IListCustomerFetchParams>(
        MN_CUSTOMERS,
      );
      return await repository
        .get(customerId)
        .then((response: ApiResponse<ICustomer>) => {
          if (response?.data) {
            return response?.data;
          }
        })
        .catch((err: AxiosError) => handleAxiosError(err));
    }
  };

  const generateDocument = async () => {
    try {
      const customer = await getCustomerData();
      if (!customer) return;
      const tags = {
        customer: customer.name.toUpperCase(),
        address: customer.address,
        postalCode: customer.postalCode,
        city: customer.city,
        state: customer.state,
        date: dayjs.utc().format('DD-MM-YYYY'),
        code: code.toUpperCase(),
        startDate: dayjs.utc(startDate).format('DD-MM-YYYY'),
        endDate: dayjs.utc(endDate).format('DD-MM-YYYY'),
        resource: resource.name,
        typeOfPayment: typeOfPayment.name,
        dailyRevenue: Number(dailyRevenue).toFixed(2),
        scheduledDays: countBusinessDays(startDate, endDate),
        technology: technology?.toUpperCase(),
      };
      const content = await loadFile('/templates/order_template.docx');
      const zip = new PizZip(content);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      // Sostituisci i tag nel documento
      doc.setData(tags);

      try {
        // Rendi effettive le sostituzioni
        doc.render();
      } catch (error) {
        const e = error as Error & { properties: any };
        console.log(JSON.stringify({ error: e }));
        return Promise.reject(error);
      }

      const out = doc.getZip().generate({
        type: 'blob',
        mimeType:
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      // Salva il documento modificato
      saveAs(out, `${code}.docx`);
    } catch (error) {
      console.error('Error generating document:', error);
    }
  };

  return (
    <div className="flex flex-col justify-center align-middle space-x-4">
      <Button
        type="button"
        onClick={generateDocument}
        disabled={
          !(
            code &&
            typeOfPayment &&
            resource &&
            startDate &&
            endDate &&
            dailyRevenue &&
            jobId
          )
        }
      >
        {t('form.createOrderDocButton')}
      </Button>
    </div>
  );
};

export default CreateOrderDocButton;
